import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { getCanonicalUrl } from "../utils/seoConfig";
import { getPostBySlug } from "../data/blogPosts";
import { formatBlogDate } from "../utils/formatBlogDate";
import "../styles/blog.scss";

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (!post) {
      navigate("/blog", { replace: true });
    }
  }, [post, navigate]);

  if (!post) {
    return null;
  }

  const isDe = i18n.language && i18n.language.startsWith("de");
  const localizedTitle =
    isDe && post.titleDe ? post.titleDe : post.title;
  const bodyText =
    post.bodyDe && isDe ? post.bodyDe : post.body;
  const blocks = bodyText.trim().split(/\n\n+/).filter(Boolean);

  const markdownImageLineRegex = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/;

  const renderTextWithLinks = (text, keyPrefix) => {
    const renderBoldText = (plainText, subKey) => {
      const boldRegex = /\*\*(.*?)\*\*/g;
      const segments = plainText.split(boldRegex);

      return segments.map((segment, segIndex) => {
        // Odd indices are captured bold groups when split with regex.
        if (segIndex % 2 === 1) {
          return (
            <strong key={`${subKey}-bold-${segIndex}`}>
              {segment}
            </strong>
          );
        }

        return (
          <React.Fragment key={`${subKey}-text-${segIndex}`}>
            {segment}
          </React.Fragment>
        );
      });
    };

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, idx) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={`${keyPrefix}-link-${idx}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        );
      }
      return renderBoldText(part, `${keyPrefix}-${idx}`);
    });
  };

  const renderBlock = (block, index) => {
    const trimmed = block.trim();
    const standaloneImageMatch = trimmed.match(markdownImageLineRegex);
    if (
      standaloneImageMatch &&
      trimmed.split(/\n/).filter((line) => line.trim().length > 0).length === 1
    ) {
      const altText = standaloneImageMatch[1] || "";
      const srcUrl = standaloneImageMatch[2].trim();

      return (
        <figure key={index} className="blog-post-figure">
          <img src={srcUrl} alt={altText} loading="lazy" />
          {altText ? (
            <figcaption className="blog-post-figure-caption">{altText}</figcaption>
          ) : null}
        </figure>
      );
    }

    const lines = block.split("\n").map((line) => line.trim());
    const paragraphLines = lines.filter(
      (line) => line.length > 0 && !line.startsWith("- ")
    );
    const listItems = lines
      .filter((line) => line.startsWith("- "))
      .map((line) => line.replace(/^- /, "").trim());

    if (listItems.length > 0 && paragraphLines.length > 0) {
      return (
        <React.Fragment key={index}>
          <p>{renderTextWithLinks(paragraphLines.join(" "), `${index}-p`)}</p>
          <ul className="blog-post-list">
            {listItems.map((item, itemIndex) => (
              <li key={`${index}-${itemIndex}`}>
                {renderTextWithLinks(item, `${index}-${itemIndex}`)}
              </li>
            ))}
          </ul>
        </React.Fragment>
      );
    }

    if (listItems.length > 0 && listItems.length === lines.length) {
      return (
        <ul key={index} className="blog-post-list">
          {listItems.map((item, itemIndex) => (
            <li key={`${index}-${itemIndex}`}>
              {renderTextWithLinks(item, `${index}-${itemIndex}`)}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p key={index}>
        {renderTextWithLinks(block, `${index}`)}
      </p>
    );
  };

  return (
    <>
      <Helmet>
        <title>{`${localizedTitle} | Predsense`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={getCanonicalUrl(`/blog/${post.slug}`)} />
      </Helmet>
      <div className="App">
        <article className="blog-post">
          <Link to="/blog" className="blog-post-back">
            ← {t("BackToBlog")}
          </Link>
          <header className="blog-post-header">
            <span className="blog-post-category">{post.category}</span>
            <h1 className="blog-post-title">{localizedTitle}</h1>
            <div className="blog-post-meta">
              <span>{post.author}</span>
              <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
            </div>
          </header>
          {post.image ? (
            <div className="blog-post-cover-wrap">
              <img
                className="blog-post-cover-img"
                src={post.image}
                alt=""
                loading="lazy"
              />
            </div>
          ) : null}
          <div className="blog-post-body">
            {blocks.map((block, i) => renderBlock(block, i))}
          </div>
        </article>
      </div>
    </>
  );
}

export default BlogPost;
